import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import client from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import ImageUpload from "../components/ImageUpload";
import GoogleBusinessCard from "../components/GoogleBusinessCard";
import GuideTab from "../components/GuideTab";
import { Plus, Trash2, Copy, ExternalLink, Check, ShoppingBag, Settings as SettingsIcon, Wallet, ListOrdered, Sparkles, AlertTriangle, Pencil, BookOpen } from "lucide-react";
import UploadProgressPie from "../components/UploadProgressPie";

const TABS = ["menu", "orders", "settings", "payments", "subscription", "guide"];

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [restaurants, setRestaurants] = useState([]);
  const [activeRestId, setActiveRestId] = useState(null);
  const [activeTab, setActiveTab] = useState("menu");
  const [newName, setNewName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("subscription_session_id");
    const esid = params.get("extra_session_id");
    const csid = params.get("commission_session_id");
    const pollSid = sid || esid || csid;
    if (!pollSid) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await client.get(`/payments/status/${pollSid}`);
        if (data.payment_status === "paid") {
          await refresh();
          const list = await client.get("/restaurants/mine");
          setRestaurants(list.data);
          navigate("/dashboard", { replace: true });
          return;
        }
      } catch (e) { /* ignore */ }
      if (attempts++ < 6) setTimeout(poll, 2000);
    };
    poll();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await client.get("/restaurants/mine");
        setRestaurants(data);
        if (data.length > 0 && !activeRestId) setActiveRestId(data[0].id);
      } catch (e) { /* ignore */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (loading) return <div className="p-10 text-center text-zinc-500">...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin") return <Navigate to="/super-admin" replace />;

  const activeRest = restaurants.find((r) => r.id === activeRestId);

  const createRestaurant = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await client.post("/restaurants", { name: newName.trim() });
      setNewName("");
      const list = await client.get("/restaurants/mine");
      setRestaurants(list.data);
      setActiveRestId(data.restaurant.id);
      if (data.is_extra) {
        alert(`${t.dashboard.extraCreatedToastTitle}\n\n${t.dashboard.extraCreatedToastSub}`);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  const payExtra = async (rid) => {
    try {
      const origin_url = window.location.origin;
      const { data } = await client.post(`/payments/subscription/extra/${rid}`, { origin_url });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  // Compute trial info
  let trialDaysLeft = null;
  if (user.subscription_status === "trial" && user.subscription_until) {
    const until = new Date(user.subscription_until);
    trialDaysLeft = Math.max(0, Math.ceil((until - new Date()) / (1000 * 60 * 60 * 24)));
  }
  // Renewal-soon banner when subscription is active and < 6 days from expiry
  let renewalDaysLeft = null;
  if (user.subscription_status === "active" && user.subscription_until) {
    const u = new Date(user.subscription_until);
    const diff = Math.ceil((u - new Date()) / (1000 * 60 * 60 * 24));
    if (diff >= 0 && diff <= 5) renewalDaysLeft = diff;
  }

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 py-10 w-full flex-1">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="chip chip-cyan inline-flex">{t.nav.dashboard}</div>
            <h1 className="font-heading text-4xl tracking-tight mt-3">{t.dashboard.welcome}, {user.name}</h1>
            <div className="text-sm text-zinc-400 mt-1 flex items-center gap-2 flex-wrap">
              <span>{t.dashboard.subscriptionTitle}:</span>
              {user.subscription_status === "trial" && <span className="chip chip-yellow !py-1 !px-2 !text-[10px]">{t.dashboard.trial}</span>}
              {user.subscription_status === "active" && <span className="chip chip-cyan !py-1 !px-2 !text-[10px]">{t.dashboard.active}</span>}
              {user.subscription_status === "inactive" && <span className="chip !py-1 !px-2 !text-[10px] text-red-400 border-red-500/30">{t.dashboard.inactive}</span>}
              {user.plan && <span className="font-mono text-xs">· {user.plan === "pro" ? t.dashboard.planPro : t.dashboard.planBasic}</span>}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t.dashboard.newRestName}
              className="input-neon w-64 !rounded-full" data-testid="new-restaurant-name" />
            <button onClick={createRestaurant} className="btn-primary" data-testid="new-restaurant-btn">
              <Plus className="w-4 h-4" /> {t.dashboard.create}
            </button>
          </div>
        </div>

        {/* Trial banner */}
        {trialDaysLeft !== null && trialDaysLeft > 0 && (
          <div className="card-neon p-5 mb-6 flex items-start gap-4" data-testid="trial-banner">
            <Sparkles className="w-6 h-6 text-neon-yellow shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-semibold">{t.dashboard.trialBannerTitle}</div>
              <div className="text-sm text-zinc-400 mt-0.5">{t.dashboard.trialBannerSub.replace("{d}", trialDaysLeft)}</div>
            </div>
            <button onClick={() => setActiveTab("subscription")} className="btn-yellow !py-1.5 text-xs" data-testid="trial-cta">
              {t.dashboard.activate}
            </button>
          </div>
        )}
        {user.subscription_status === "inactive" && (
          <div className="card-neon p-5 mb-6 flex items-start gap-4 border-red-500/30" data-testid="trial-expired-banner">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-semibold text-red-400">{t.dashboard.trialExpired}</div>
            </div>
            <button onClick={() => setActiveTab("subscription")} className="btn-yellow !py-1.5 text-xs" data-testid="expired-cta">
              {t.dashboard.activate}
            </button>
          </div>
        )}
        {renewalDaysLeft !== null && (
          <div className="card-neon p-5 mb-6 flex items-start gap-4 border-amber-500/30" data-testid="renewal-soon-banner">
            <Sparkles className="w-6 h-6 text-neon-yellow shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-semibold">{t.dashboard.renewalSoonTitle}</div>
              <div className="text-sm text-zinc-400 mt-0.5">{t.dashboard.renewalSoonSub.replace("{d}", renewalDaysLeft)}</div>
            </div>
            <button onClick={() => setActiveTab("subscription")} className="btn-yellow !py-1.5 text-xs" data-testid="renewal-cta">
              {t.dashboard.renew}
            </button>
          </div>
        )}

        <CommissionBanner t={t} />

        {restaurants.length === 0 ? (
          <div className="card-neon p-12 text-center" data-testid="empty-restaurants">
            <p className="text-zinc-400">{t.dashboard.noRestaurants}</p>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-6 overflow-x-auto scrollbar-thin">
              {restaurants.map((r) => {
                const lock = r.lock || {};
                let pill = null;
                if (lock.blocked) {
                  pill = <span className="ml-2 chip !py-0 !px-1.5 !text-[9px] text-red-300 border-red-500/40 bg-red-500/10" data-testid={`restaurant-tab-pill-${r.id}`}>{t.dashboard.extraBlockedBadge}</span>;
                } else if (lock.reason === "grace") {
                  pill = <span className="ml-2 chip chip-yellow !py-0 !px-1.5 !text-[9px]" data-testid={`restaurant-tab-pill-${r.id}`}>{t.dashboard.extraGraceBadge} {lock.days_left ?? 0}d</span>;
                } else if (lock.reason === "paid") {
                  pill = <span className="ml-2 chip chip-cyan !py-0 !px-1.5 !text-[9px]" data-testid={`restaurant-tab-pill-${r.id}`}>{t.dashboard.extraPaidBadge}</span>;
                }
                return (
                  <button key={r.id} onClick={() => setActiveRestId(r.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium border whitespace-nowrap transition inline-flex items-center ${
                      activeRestId === r.id ? "bg-neon-cyan text-[#06061a] border-[#00E5FF] glow-cyan" : "bg-deep-card border-line text-zinc-300 hover:text-neon-cyan"
                    }`} data-testid={`restaurant-tab-${r.id}`}>{r.name}{pill}</button>
                );
              })}
            </div>

            {activeRest && (
              <>
                <ExtraRestaurantBanner rest={activeRest} t={t} onPay={() => payExtra(activeRest.id)} />
                <PublicLink rest={activeRest} t={t} />
                <GoogleBusinessCard rest={activeRest} t={t} />

                <div className="flex gap-2 mt-6 mb-6 flex-wrap">
                  {TABS.map((tab) => (
                    <button key={tab} onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 rounded-full text-xs font-semibold border font-mono uppercase tracking-widest transition ${
                        activeTab === tab
                          ? "bg-neon-violet text-[#06061a] border-neon glow-violet"
                          : tab === "guide"
                            ? "bg-amber-500/15 border-amber-400/60 text-amber-200 hover:bg-amber-500/25"
                            : "bg-deep-card border-line text-zinc-300 hover:text-neon-violet"
                      }`} data-testid={`tab-${tab}`}>
                      {tab === "menu" && <ShoppingBag className="w-3.5 h-3.5 inline mr-1.5" />}
                      {tab === "orders" && <ListOrdered className="w-3.5 h-3.5 inline mr-1.5" />}
                      {tab === "settings" && <SettingsIcon className="w-3.5 h-3.5 inline mr-1.5" />}
                      {tab === "payments" && <Wallet className="w-3.5 h-3.5 inline mr-1.5" />}
                      {tab === "subscription" && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
                      {tab === "guide" && <BookOpen className="w-4 h-4 inline mr-1.5" />}
                      {t.dashboard[tab]}
                    </button>
                  ))}
                </div>

                {activeTab === "menu" && <MenuTab rest={activeRest} t={t} />}
                {activeTab === "orders" && <OrdersTab t={t} restaurantId={activeRest.id} />}
                {activeTab === "settings" && <SettingsTab rest={activeRest} t={t} onSaved={async () => {
                  const list = await client.get("/restaurants/mine");
                  setRestaurants(list.data);
                }} onDeleted={async () => {
                  const list = await client.get("/restaurants/mine");
                  setRestaurants(list.data);
                  setActiveRestId(list.data[0]?.id || null);
                  setActiveTab("menu");
                }} />}
                {activeTab === "payments" && <PaymentsTab rest={activeRest} t={t} onSaved={async () => {
                  const list = await client.get("/restaurants/mine");
                  setRestaurants(list.data);
                }} />}
                {activeTab === "subscription" && <SubscriptionTab t={t} user={user} />}
                {activeTab === "guide" && <GuideTab t={t} />}
              </>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

function ExtraRestaurantBanner({ rest, t, onPay }) {
  const lock = rest.lock || {};
  if (!lock.is_extra) return null;
  if (lock.reason === "grace") {
    return (
      <div className="card-neon p-5 mb-4 flex items-start gap-4 glow-yellow" data-testid={`extra-grace-${rest.id}`}>
        <div className="w-10 h-10 rounded-xl bg-neon-yellow text-[#06061a] flex items-center justify-center shrink-0 font-heading font-black">!</div>
        <div className="flex-1">
          <div className="font-heading font-semibold">{t.dashboard.extraGraceTitle}</div>
          <div className="text-sm text-zinc-400 mt-0.5">{t.dashboard.extraGraceSub.replace("{d}", lock.days_left ?? 0)}</div>
        </div>
        <button onClick={onPay} className="btn-yellow !py-1.5 text-xs" data-testid={`pay-extra-${rest.id}`}>
          {t.dashboard.payExtraNow}
        </button>
      </div>
    );
  }
  if (lock.blocked && (lock.reason === "extra_unpaid" || lock.reason === "manually_blocked")) {
    return (
      <div className="card-neon p-5 mb-4 flex items-start gap-4 border-red-500/40" data-testid={`extra-blocked-${rest.id}`}>
        <div className="w-10 h-10 rounded-xl bg-red-500 text-white flex items-center justify-center shrink-0 font-heading font-black">×</div>
        <div className="flex-1">
          <div className="font-heading font-semibold text-red-400">
            {lock.reason === "manually_blocked" ? t.dashboard.lockedManually : t.dashboard.extraExpiredTitle}
          </div>
          <div className="text-sm text-zinc-400 mt-0.5">
            {lock.reason === "manually_blocked" ? "—" : t.dashboard.extraExpiredSub}
          </div>
        </div>
        {lock.reason === "extra_unpaid" && (
          <button onClick={onPay} className="btn-yellow !py-1.5 text-xs" data-testid={`pay-extra-${rest.id}`}>
            {t.dashboard.payExtraNow}
          </button>
        )}
      </div>
    );
  }
  if (lock.reason === "paid" && lock.extra_paid_until) {
    return (
      <div className="card-neon p-3 mb-4 flex items-center justify-between gap-3" data-testid={`extra-paid-${rest.id}`}>
        <span className="text-xs text-zinc-400 font-mono">
          <span className="chip chip-cyan !py-0.5 !px-2 !text-[10px] mr-2">{t.dashboard.extraPaidBadge}</span>
          {t.dashboard.extraPaidUntil} {new Date(lock.extra_paid_until).toLocaleDateString()}
        </span>
      </div>
    );
  }
  return null;
}


function PublicLink({ rest, t }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/r/${rest.slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="card-neon p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="public-link-box">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">{t.dashboard.yourLink}</div>
        <div className="font-mono text-sm truncate mt-1 text-zinc-200">{url}</div>
      </div>
      <div className="flex gap-2">
        <button onClick={copy} className="btn-outline text-xs" data-testid="copy-link-btn">
          {copied ? <><Check className="w-3.5 h-3.5 text-neon-cyan" /> {t.dashboard.copied}</> : <><Copy className="w-3.5 h-3.5" /> {t.dashboard.copyLink}</>}
        </button>
        <a href={url} target="_blank" rel="noreferrer" className="btn-primary !py-1.5 text-xs" data-testid="open-link-btn">
          <ExternalLink className="w-3.5 h-3.5" /> {t.dashboard.preview}
        </a>
      </div>
    </div>
  );
}

const ALLERGENS_PRESET = ["glutine", "lattosio", "uova", "frutta a guscio", "pesce", "crostacei", "soia", "sesamo", "sedano", "mostarda", "lupini", "molluschi", "arachidi", "anidride solforosa"];

function MenuTab({ rest, t }) {
  const [dishes, setDishes] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", price: "", image_url: "", category: "Menu", ingredients: "", allergens: [] });
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(null); // {preview: [...], file: name} | null
  const [importBusy, setImportBusy] = useState(false);
  const [importPhase, setImportPhase] = useState(""); // "upload" | "ai" | "insert"
  const [importProgress, setImportProgress] = useState(0); // 0..100 for upload, null for indeterminate AI
  const [importErr, setImportErr] = useState("");

  const load = async () => {
    const { data } = await client.get("/dishes", { params: { restaurant_id: rest.id } });
    setDishes(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rest.id]);

  const add = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) return;
    await client.post("/dishes", { ...form, restaurant_id: rest.id, price: parseFloat(form.price) });
    setForm({ name: "", description: "", price: "", image_url: "", category: "Menu", ingredients: "", allergens: [] });
    load();
  };
  const del = async (id) => { await client.delete(`/dishes/${id}`); load(); };

  const onPickPdf = async (file) => {
    if (!file) return;
    setImportErr("");
    setImportBusy(true);
    setImportPhase("upload");
    setImportProgress(0);
    try {
      const fd = new FormData();
      fd.append("restaurant_id", rest.id);
      fd.append("file", file);
      // 1) START job (multipart upload)
      const { data: startData } = await client.post("/dishes/import-pdf-start", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const p = Math.round((evt.loaded * 100) / evt.total);
            setImportProgress(Math.min(99, p));
          }
        },
      });
      const jobId = startData.job_id;
      // 2) Poll status every 3s up to 5 minutes
      setImportPhase("ai");
      setImportProgress(null);
      let attempts = 0;
      while (attempts < 100) {
        await new Promise((r) => setTimeout(r, 3000));
        attempts++;
        const { data: st } = await client.get(`/dishes/import-pdf-status/${jobId}`);
        if (st.status === "done") {
          setImporting({ dishes: st.dishes || [], fileName: file.name });
          return;
        }
        if (st.status === "error") {
          throw new Error(st.error || "AI error");
        }
      }
      throw new Error("Timeout (5 min). Riprova con un PDF più piccolo.");
    } catch (e) {
      setImportErr(e?.response?.data?.detail || e?.message || "Errore");
    } finally {
      setImportBusy(false);
      setImportPhase("");
      setImportProgress(0);
    }
  };
  const confirmImport = async () => {
    setImportBusy(true);
    setImportPhase("insert");
    setImportProgress(null);
    try {
      await client.post("/dishes/import-bulk", { restaurant_id: rest.id, dishes: importing.dishes });
      setImporting(null);
      load();
    } catch (e) {
      setImportErr(e?.response?.data?.detail || "Errore");
    } finally {
      setImportBusy(false);
      setImportPhase("");
      setImportProgress(0);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-1 space-y-4">
        <form onSubmit={add} className="card-neon p-6 space-y-3" data-testid="add-dish-form">
          <h3 className="font-heading font-semibold text-lg">{t.dashboard.addDish}</h3>
          <Input label={t.dashboard.dishName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="dish-name" />
          <Input label={t.dashboard.dishDesc} value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="dish-desc" />
          <Input label={t.dashboard.dishPrice} type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testId="dish-price" />
          <Input label={t.dashboard.dishIngredients || "Ingredienti"} value={form.ingredients} onChange={(v) => setForm({ ...form, ingredients: v })} testId="dish-ingredients" />
          <AllergensField value={form.allergens} onChange={(v) => setForm({ ...form, allergens: v })} t={t} testId="dish-allergens" />
          <ImageUpload label={t.dashboard.dishImage} value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="dish-image" aspect="video" />
          <Input label={t.dashboard.dishCategory} value={form.category} onChange={(v) => setForm({ ...form, category: v })} testId="dish-category" />
          <button type="submit" className="btn-primary w-full justify-center" data-testid="dish-save-btn">{t.dashboard.save}</button>
        </form>

        <div className="card-neon p-6" data-testid="menu-import-card">
          <h3 className="font-heading font-semibold text-lg">{t.dashboard.menuImportTitle || "Importa menu PDF"}</h3>
          <p className="text-xs text-zinc-400 mt-1 mb-3">{t.dashboard.menuImportSub || "Carica un PDF del tuo menu: l'AI riconosce nome, descrizione, ingredienti e allergeni."}</p>
          {importBusy ? (
            <div className="py-3 flex flex-col items-center justify-center" data-testid="menu-import-progress">
              <UploadProgressPie
                percent={importProgress}
                size={88}
                label={importPhase === "upload" ? (t.dashboard.uploadingPdf || "Caricamento PDF") : importPhase === "ai" ? (t.dashboard.aiProcessing || "AI in lavorazione") : importPhase === "insert" ? (t.dashboard.savingDishes || "Salvataggio piatti") : ""}
                testId="menu-import-pie"
              />
              <p className="text-[10px] text-zinc-500 font-mono mt-2 text-center">
                {importPhase === "ai" ? (t.dashboard.aiHint || "Gemini sta analizzando il PDF, può richiedere 10-20s") : ""}
              </p>
            </div>
          ) : (
            <label className="btn-outline w-full justify-center cursor-pointer" data-testid="menu-pdf-pick">
              {t.dashboard.menuPickPdf || "Scegli PDF"}
              <input type="file" accept="application/pdf" className="hidden" onChange={(e) => onPickPdf(e.target.files?.[0])} data-testid="menu-pdf-input" />
            </label>
          )}
          {importErr && <div className="text-xs text-red-400 mt-3" data-testid="menu-import-error">{importErr}</div>}
        </div>
      </div>

      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
        {dishes.length === 0 && <div className="card-neon p-6 text-zinc-500 sm:col-span-2" data-testid="no-dishes">{t.dashboard.noDishes}</div>}
        {dishes.map((d) => (
          <div key={d.id} className="card-neon overflow-hidden" data-testid={`dish-card-${d.id}`}>
            {d.image_url && <img src={d.image_url} alt={d.name} className="w-full h-40 object-cover" />}
            <div className="p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-neon-violet font-mono">{d.category}</div>
              <h4 className="font-heading font-semibold text-lg mt-1">{d.name}</h4>
              {d.description && <p className="text-sm text-zinc-400 mt-1">{d.description}</p>}
              {d.ingredients && <p className="text-xs text-zinc-500 mt-1 font-mono">{t.dashboard.dishIngredients || "Ingredienti"}: {d.ingredients}</p>}
              {Array.isArray(d.allergens) && d.allergens.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`dish-allergens-${d.id}`}>
                  {d.allergens.map((a) => (
                    <span key={a} className="chip !text-[9px] !py-0.5 !px-1.5 border-amber-500/40 text-amber-300">{a}</span>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-center justify-between">
                <div className="font-heading font-bold text-neon-cyan">€ {Number(d.price).toFixed(2)}</div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(d)} className="text-zinc-500 hover:text-neon-cyan" data-testid={`dish-edit-${d.id}`} title={t.dashboard.edit || "Modifica"}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => del(d.id)} className="text-zinc-500 hover:text-red-400" data-testid={`dish-delete-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditDishModal
          dish={editing}
          t={t}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}

      {importing && (
        <ImportPreviewModal
          fileName={importing.fileName}
          dishes={importing.dishes}
          onChange={(updated) => setImporting({ ...importing, dishes: updated })}
          onConfirm={confirmImport}
          onClose={() => setImporting(null)}
          busy={importBusy}
          busyPhase={importPhase}
          t={t}
        />
      )}
    </div>
  );
}

function AllergensField({ value, onChange, t, testId }) {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const toggle = (a) => {
    const next = list.includes(a) ? list.filter((x) => x !== a) : [...list, a];
    onChange(next);
  };
  const addCustom = () => {
    const v = draft.trim().toLowerCase();
    if (!v || list.includes(v)) return;
    onChange([...list, v]);
    setDraft("");
  };
  return (
    <div data-testid={testId}>
      <label className="input-label">{t.dashboard.dishAllergens || "Allergeni"}</label>
      <div className="flex flex-wrap gap-1 mt-1">
        {ALLERGENS_PRESET.map((a) => (
          <button key={a} type="button" onClick={() => toggle(a)}
            className={`chip !text-[10px] !py-1 !px-2 ${list.includes(a) ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "text-zinc-400"}`}
            data-testid={`${testId}-${a}`}>
            {a}
          </button>
        ))}
      </div>
      <div className="flex gap-1 mt-2">
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="altro allergene…" className="input-neon !py-1.5 text-xs" data-testid={`${testId}-input`} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }} />
        <button type="button" onClick={addCustom} className="btn-outline !py-1 !px-2 text-xs" data-testid={`${testId}-add`}>+</button>
      </div>
      {list.filter((a) => !ALLERGENS_PRESET.includes(a)).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {list.filter((a) => !ALLERGENS_PRESET.includes(a)).map((a) => (
            <span key={a} className="chip !text-[10px] !py-0.5 !px-2 border-amber-500/40 text-amber-300">
              {a} <button type="button" onClick={() => toggle(a)} className="ml-1 text-zinc-500 hover:text-red-400">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function EditDishModal({ dish, t, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: dish.name || "",
    description: dish.description || "",
    price: dish.price ?? "",
    image_url: dish.image_url || "",
    category: dish.category || "Menu",
    ingredients: dish.ingredients || "",
    allergens: Array.isArray(dish.allergens) ? dish.allergens : [],
  });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.put(`/dishes/${dish.id}`, { ...form, price: parseFloat(form.price || 0) });
      onSaved && onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 backdrop-blur-sm">
      <form onSubmit={save} className="bg-deep-card border border-line w-full max-w-lg rounded-t-3xl sm:rounded-3xl p-7 max-h-[92vh] overflow-auto" data-testid={`dish-edit-modal-${dish.id}`}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-heading text-2xl">{t.dashboard.editDish || "Modifica piatto"}</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 text-xl hover:text-zinc-200" data-testid="dish-edit-close">×</button>
        </div>
        <div className="space-y-3">
          <Input label={t.dashboard.dishName} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="edit-dish-name" />
          <Input label={t.dashboard.dishDesc} value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="edit-dish-desc" />
          <Input label={t.dashboard.dishPrice} type="number" step="0.01" value={form.price} onChange={(v) => setForm({ ...form, price: v })} testId="edit-dish-price" />
          <Input label={t.dashboard.dishIngredients || "Ingredienti"} value={form.ingredients} onChange={(v) => setForm({ ...form, ingredients: v })} testId="edit-dish-ingredients" />
          <AllergensField value={form.allergens} onChange={(v) => setForm({ ...form, allergens: v })} t={t} testId="edit-dish-allergens" />
          <ImageUpload label={t.dashboard.dishImage} value={form.image_url} onChange={(v) => setForm({ ...form, image_url: v })} testId="edit-dish-image" aspect="video" />
          <Input label={t.dashboard.dishCategory} value={form.category} onChange={(v) => setForm({ ...form, category: v })} testId="edit-dish-category" />
        </div>
        <button type="submit" disabled={saving} className="btn-primary w-full justify-center mt-5" data-testid={`dish-edit-save-${dish.id}`}>
          {saving ? "…" : t.dashboard.save}
        </button>
      </form>
    </div>
  );
}

function ImportPreviewModal({ fileName, dishes, onChange, onConfirm, onClose, busy, busyPhase, t }) {
  const update = (idx, patch) => {
    const next = dishes.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const remove = (idx) => {
    const next = dishes.slice();
    next.splice(idx, 1);
    onChange(next);
  };
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 backdrop-blur-sm">
      <div className="bg-deep-card border border-line w-full max-w-3xl rounded-t-3xl sm:rounded-3xl p-7 max-h-[92vh] overflow-auto" data-testid="menu-import-preview">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h2 className="font-heading text-2xl">{t.dashboard.menuImportTitle || "Importa menu PDF"}</h2>
          <button type="button" onClick={onClose} className="text-zinc-500 text-xl hover:text-zinc-200" data-testid="menu-import-close">×</button>
        </div>
        <p className="text-xs text-zinc-400 mb-5 font-mono">{fileName} · {dishes.length} {t.dashboard.dishesFound || "piatti trovati"}</p>
        {dishes.length === 0 && <div className="text-zinc-500 text-sm" data-testid="menu-import-empty">{t.dashboard.menuImportEmpty || "Nessun piatto riconosciuto. Riprova con un PDF più chiaro."}</div>}
        <div className="space-y-3 max-h-[55vh] overflow-auto pr-2" data-testid="menu-import-list">
          {dishes.map((d, i) => (
            <div key={i} className="card-neon p-4 space-y-2" data-testid={`menu-import-item-${i}`}>
              <div className="flex items-center justify-between gap-2">
                <input value={d.name} onChange={(e) => update(i, { name: e.target.value })} className="input-neon !py-1 text-sm font-semibold flex-1" data-testid={`menu-import-name-${i}`} />
                <button type="button" onClick={() => remove(i)} className="text-zinc-500 hover:text-red-400" data-testid={`menu-import-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea value={d.description || ""} onChange={(e) => update(i, { description: e.target.value })} className="input-neon !py-1 text-xs w-full" rows={2} data-testid={`menu-import-desc-${i}`} />
              <div className="grid grid-cols-2 gap-2">
                <input value={d.category || ""} onChange={(e) => update(i, { category: e.target.value })} placeholder="Categoria" className="input-neon !py-1 text-xs" data-testid={`menu-import-cat-${i}`} />
                <input type="number" step="0.01" value={d.price || 0} onChange={(e) => update(i, { price: parseFloat(e.target.value || 0) })} placeholder="€" className="input-neon !py-1 text-xs" data-testid={`menu-import-price-${i}`} />
              </div>
              {d.ingredients && <div className="text-[10px] text-zinc-500 font-mono">{d.ingredients}</div>}
              {Array.isArray(d.allergens) && d.allergens.length > 0 && (
                <div className="flex flex-wrap gap-1">{d.allergens.map((a) => (
                  <span key={a} className="chip !text-[9px] !py-0.5 !px-1.5 border-amber-500/40 text-amber-300">{a}</span>
                ))}</div>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-5 flex-wrap items-center">
          <button onClick={onConfirm} disabled={busy || dishes.length === 0} className="btn-primary flex-1 justify-center" data-testid="menu-import-confirm">
            {busy ? "…" : (t.dashboard.menuImportConfirm || `Aggiungi tutti (${dishes.length})`)}
          </button>
          {busy && busyPhase === "insert" && (
            <UploadProgressPie percent={null} size={40} testId="menu-import-confirm-pie" />
          )}
          <button onClick={onClose} disabled={busy} className="btn-outline" data-testid="menu-import-cancel">{t.dashboard.cancel || "Annulla"}</button>
        </div>
      </div>
    </div>
  );
}

function OrdersTab({ t, restaurantId }) {
  const [orders, setOrders] = useState([]);
  const lastNotifiedRef = React.useRef(null);
  const load = async () => {
    const { data } = await client.get("/orders/mine");
    const filtered = data.filter((o) => o.restaurant_id === restaurantId);
    setOrders(filtered);
    // Ring bell for newly arrived paid orders since last check
    try {
      const newPaid = filtered.filter((o) => o.payment_status === "paid" && o.status === "paid");
      if (newPaid.length > 0) {
        const latest = newPaid[0].paid_at || newPaid[0].created_at;
        if (lastNotifiedRef.current && latest && latest > lastNotifiedRef.current) {
          playBell();
        }
        lastNotifiedRef.current = latest || lastNotifiedRef.current;
      }
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [restaurantId]);
  useEffect(() => {
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  // eslint-disable-next-line
  }, [restaurantId]);

  const acceptOrder = async (id, eta) => { await client.put(`/orders/${id}/accept`, { eta_minutes: eta ? parseInt(eta) : null }); load(); };
  const rejectOrder = async (id) => {
    const reason = window.prompt("Motivo del rifiuto (verrà mostrato al cliente, rimborso automatico se Stripe Connect è attivo):", "");
    if (reason === null) return;
    await client.put(`/orders/${id}/reject`, { reason });
    load();
  };
  const advanceOrder = async (id, eta) => { await client.put(`/orders/${id}/advance`, { eta_minutes: eta ? parseInt(eta) : null }); load(); };
  const setEta = async (id, eta) => { await client.put(`/orders/${id}/eta`, { eta_minutes: parseInt(eta) }); load(); };
  const markPaid = async (id) => {
    if (!window.confirm(t.dashboard.confirmMarkPaid || "Confermi di aver ricevuto il pagamento per questo ordine?")) return;
    try { await client.put(`/orders/${id}/mark-paid`); load(); } catch (e) { alert(e?.response?.data?.detail || "Errore"); }
  };

  if (orders.length === 0) return <div className="card-neon p-6 text-zinc-500" data-testid="no-orders">{t.dashboard.noOrders}</div>;
  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o) => <OrderCard key={o.id} o={o} t={t} onAccept={acceptOrder} onReject={rejectOrder} onAdvance={advanceOrder} onEta={setEta} onMarkPaid={markPaid} />)}
    </div>
  );
}

function playBell() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "triangle";
    o.frequency.setValueAtTime(880, ctx.currentTime);
    o.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.35);
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.35, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.6);
    o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.65);
  } catch (e) { /* ignore */ }
}

function OrderCard({ o, t, onAccept, onReject, onAdvance, onEta, onMarkPaid }) {
  const [eta, setEtaLocal] = useState(o.eta_minutes || "");
  const isPickup = (o.order_type || "delivery") === "pickup";
  const flow = isPickup
    ? ["paid", "accepted", "preparing", "ready", "delivered"]
    : ["paid", "accepted", "preparing", "out_for_delivery", "delivered"];
  const stepIdx = flow.indexOf(o.status);
  const advanceLabel =
    o.status === "accepted" ? t.dashboard.markPreparing :
    o.status === "preparing" ? (isPickup ? t.dashboard.markReady : t.dashboard.markOut) :
    null;

  return (
    <div className="card-neon p-5" data-testid={`order-${o.id}`}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">#{o.id.slice(-6)}</div>
            <span className={`chip !text-[9px] !py-0 !px-1.5 ${isPickup ? "chip-yellow" : "chip-cyan"}`} data-testid={`order-${o.id}-type`}>
              {isPickup ? (t.storefront.pickup || "RITIRO") : (t.storefront.delivery || "CONSEGNA")}
            </span>
            {o.payment_method === "cash" && (
              <span className="chip !text-[9px] !py-0 !px-1.5 border-amber-500/40 text-amber-300" data-testid={`order-${o.id}-cash`}>
                {t.storefront.cashPayment || "PAGAMENTO ALL'ARRIVO"}
              </span>
            )}
            {o.eta_minutes && <span className="chip !text-[9px] !py-0 !px-1.5">TIME {o.eta_minutes}min</span>}
          </div>
          <div className="font-heading font-semibold mt-1">{o.customer_name} · {o.customer_phone}</div>
          {/* Order-type specific info row */}
          {isPickup ? (
            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#FFD600]/40 bg-[#FFD600]/5 text-amber-200 text-xs font-mono uppercase tracking-widest" data-testid={`order-${o.id}-pickup-info`}>
              <ShoppingBag className="w-3.5 h-3.5" /> {t.dashboard.pickupInStore || "Ritiro in negozio"}
            </div>
          ) : (
            <div className="text-sm text-zinc-400 mt-1 inline-flex items-start gap-1.5" data-testid={`order-${o.id}-delivery-addr`}>
              <span className="text-[10px] uppercase tracking-widest text-neon-cyan font-mono mt-0.5">{t.dashboard.deliverTo || "Consegnare a"}:</span>
              <span>{o.address}</span>
            </div>
          )}
          {o.notes && (
            <div className="mt-2 p-2.5 rounded-lg border border-yellow-500/40 bg-yellow-500/10" data-testid={`order-${o.id}-notes`}>
              <div className="text-[9px] uppercase tracking-widest text-yellow-300 font-mono">📝 {t.dashboard.customerNotes || "Note del cliente"}</div>
              <div className="text-sm text-yellow-100 mt-0.5 font-medium">{o.notes}</div>
            </div>
          )}
          {(o.customer_total_points > 0 || o.points_awarded > 0) && (
            <div className="text-[10px] text-neon-cyan mt-1.5 font-mono" data-testid={`order-${o.id}-points`}>
              ⭐ {t.dashboard.pointsAwarded || "Punti per questo ordine"}: <span className="font-bold">{o.points_awarded || 0}</span> · {t.dashboard.customerPoints || "Punti cliente totali"}: <span className="font-bold text-neon-yellow">{o.customer_total_points || 0}</span>
            </div>
          )}
          <div className="text-xs text-zinc-500 mt-1 font-mono">{new Date(o.created_at).toLocaleString()}</div>
        </div>
        <div className="text-right">
          <div className="font-heading font-bold text-2xl text-neon-yellow">€ {Number(o.total).toFixed(2)}</div>
          <div className="text-[10px] text-zinc-500 font-mono">6% RENOVA: €{Number(o.commission_renova).toFixed(2)}</div>
          <div className="text-[10px] text-neon-cyan font-mono">PAYOUT: €{Number(o.restaurant_payout).toFixed(2)}</div>
        </div>
      </div>
      <ul className="text-sm text-zinc-300 mt-3 space-y-1">
        {o.items.map((it, i) => (<li key={i}>{it.qty}× {it.name} — € {(it.price * it.qty).toFixed(2)}</li>))}
      </ul>

      {/* Status timeline */}
      <div className="mt-4 flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono" data-testid={`order-${o.id}-timeline`}>
        {flow.map((st, i) => (
          <React.Fragment key={st}>
            <span className={`px-2 py-0.5 rounded-full border ${i <= stepIdx ? "border-neon-cyan text-neon-cyan" : "border-line text-zinc-600"}`}>
              {t.dashboard.mark[st] || st}
            </span>
            {i < flow.length - 1 && <span className={`flex-1 h-px ${i < stepIdx ? "bg-neon-cyan" : "bg-line"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-4 flex gap-2 flex-wrap items-center">
        {o.status === "paid" && (
          <>
            {(o.payment_status === "pending_paypal" || o.payment_status === "pending_satispay" || o.payment_status === "pending_cash") && (
              <button
                onClick={() => onMarkPaid(o.id)}
                className="btn-outline !py-1.5 !px-3 text-xs !border-green-500/60 !text-green-300 hover:!bg-green-500/10"
                data-testid={`order-${o.id}-mark-paid`}
              >
                ✓ {o.payment_method === "paypal" ? (t.dashboard.markPaidPaypal || "Pagato via PayPal") : o.payment_method === "satispay" ? (t.dashboard.markPaidSatispay || "Pagato via Satispay") : (t.dashboard.markPaidCash || "Pagato in contanti")}
              </button>
            )}
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-zinc-500 uppercase font-mono">TIME</span>
              <input type="number" min={1} value={eta} onChange={(e) => setEtaLocal(e.target.value)} placeholder="30" className="w-16 input-neon !py-1 !text-xs" data-testid={`order-${o.id}-eta-input`} />
              <span className="text-[10px] text-zinc-500">min</span>
            </div>
            {[15, 30, 45].map((q) => (
              <button key={q} type="button" onClick={() => setEtaLocal(q)} className="text-[10px] px-2 py-1 rounded border border-line text-zinc-400 hover:text-neon-cyan" data-testid={`order-${o.id}-eta-${q}`}>{q}'</button>
            ))}
            <button onClick={() => onAccept(o.id, eta)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`order-${o.id}-accept`}>{t.dashboard.accept}</button>
            <button onClick={() => onReject(o.id)} className="btn-outline !py-1.5 !px-3 text-xs border-red-500/40 text-red-300" data-testid={`order-${o.id}-reject`}>{t.dashboard.reject}</button>
          </>
        )}
        {(o.status === "accepted" || o.status === "preparing") && advanceLabel && (
          <>
            <button onClick={() => onAdvance(o.id, eta)} className="btn-primary !py-1.5 !px-3 text-xs" data-testid={`order-${o.id}-advance`}>{advanceLabel}</button>
            {o.eta_minutes && (
              <button onClick={() => onEta(o.id, eta || o.eta_minutes)} className="btn-outline !py-1.5 !px-3 text-xs" data-testid={`order-${o.id}-update-eta`}>{t.dashboard.updateTime || t.dashboard.updateEta}</button>
            )}
          </>
        )}
        {(o.status === "ready" || o.status === "out_for_delivery") && (
          <span className="chip chip-yellow !text-[10px]" data-testid={`order-${o.id}-waiting-customer`}>{t.dashboard.waitingCustomer}</span>
        )}
        {o.status === "delivered" && <span className="chip chip-cyan !text-[10px]">{t.dashboard.mark.delivered}</span>}
        {o.status === "rejected" && (
          <span className="chip !text-[10px] border-red-500/40 text-red-300" data-testid={`order-${o.id}-rejected-badge`}>
            {t.dashboard.mark.rejected || "Rifiutato"}
            {o.refund_status && ` · ${o.refund_status === "manual_required" ? (t.dashboard.refundManual || "rimborso manuale") : o.refund_status}`}
          </span>
        )}
      </div>
      {o.reject_reason && <div className="mt-2 text-xs text-zinc-500 italic" data-testid={`order-${o.id}-reject-reason`}>↳ {o.reject_reason}</div>}
    </div>
  );
}

function Input({ label, value, onChange, type = "text", step, testId }) {
  return (
    <div>
      <label className="input-label">{label}</label>
      <input type={type} step={step} value={value || ""} onChange={(e) => onChange(e.target.value)} className="input-neon" data-testid={testId} />
    </div>
  );
}

function ToggleRow({ label, value, onChange, testId }) {
  return (
    <label className="flex items-center justify-between gap-4 py-2">
      <span className="text-sm font-medium text-zinc-300">{label}</span>
      <button type="button" onClick={() => onChange(!value)}
        className={`relative w-11 h-6 rounded-full transition ${value ? "bg-neon-cyan glow-cyan" : "bg-zinc-700"}`} data-testid={testId}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${value ? "translate-x-5" : ""}`} />
      </button>
    </label>
  );
}

function SettingsTab({ rest, t, onSaved, onDeleted }) {
  const [form, setForm] = useState(rest);
  const [saved, setSaved] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  useEffect(() => { setForm(rest); }, [rest]);
  const isOpen = form.is_open !== false; // default true
  const save = async () => {
    const payload = { ...form };
    payload.delivery_time_min = parseInt(payload.delivery_time_min || 0);
    payload.delivery_fee = parseFloat(payload.delivery_fee || 0);
    payload.urgency_fee = parseFloat(payload.urgency_fee || 0);
    payload.promo_discount_percent = parseFloat(payload.promo_discount_percent || 0);
    if (payload.loyalty) {
      payload.loyalty = {
        enabled: !!payload.loyalty.enabled,
        points_per_euro: parseFloat(payload.loyalty.points_per_euro || 0),
        euro_per_point: parseFloat(payload.loyalty.euro_per_point || 0),
        min_points_to_redeem: parseInt(payload.loyalty.min_points_to_redeem || 0),
      };
    }
    await client.put(`/restaurants/${rest.id}`, payload);
    setSaved(true); setTimeout(() => setSaved(false), 1500);
    onSaved && onSaved();
  };
  const toggleOpen = async () => {
    setToggling(true);
    try {
      const next = !isOpen;
      await client.put(`/restaurants/${rest.id}`, { is_open: next });
      setForm({ ...form, is_open: next });
      onSaved && onSaved();
    } finally {
      setToggling(false);
    }
  };
  const deleteRest = async () => {
    setDeleting(true);
    try {
      await client.delete(`/restaurants/${rest.id}`);
      onDeleted && onDeleted();
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore");
      setDeleting(false);
    }
  };
  return (
    <>
    <div className={`card-neon p-6 mb-6 ${isOpen ? "border-neon-cyan/40" : "border-red-500/40"}`} data-testid="open-close-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3">
            <span className={`inline-block w-3 h-3 rounded-full ${isOpen ? "bg-neon-cyan glow-cyan animate-pulse" : "bg-red-500"}`} />
            <h3 className="font-heading font-semibold text-xl">
              {isOpen ? (t.dashboard.statusOpen || "Ristorante APERTO") : (t.dashboard.statusClosed || "Ristorante CHIUSO")}
            </h3>
          </div>
          <p className="text-sm text-zinc-400 mt-1">
            {isOpen ? (t.dashboard.openHint || "Stai accettando ordini dai clienti.") : (t.dashboard.closedHint || "Nessun ordine può essere effettuato.")}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isOpen ? (
            <button onClick={toggleOpen} disabled={toggling} className="btn-outline border-red-500/40 text-red-300 hover:!bg-red-500/10" data-testid="close-restaurant-btn">
              {toggling ? "…" : (t.dashboard.closeRestaurant || "Chiudi ristorante")}
            </button>
          ) : (
            <button onClick={toggleOpen} disabled={toggling} className="btn-primary" data-testid="open-restaurant-btn">
              {toggling ? "…" : (t.dashboard.openRestaurant || "Apri ristorante")}
            </button>
          )}
          <button onClick={() => setConfirmDel(true)} className="btn-outline border-red-500/40 text-red-300 hover:!bg-red-500/10" data-testid="delete-restaurant-btn">
            <Trash2 className="w-4 h-4" /> {t.dashboard.deleteRestaurant || "Cancella ristorante"}
          </button>
        </div>
      </div>
    </div>

    {confirmDel && (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6 backdrop-blur-sm" data-testid="delete-confirm-modal">
        <div className="bg-deep-card border border-red-500/40 max-w-md w-full rounded-3xl p-7">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-8 h-8 text-red-400 shrink-0" />
            <h2 className="font-heading text-2xl">{t.dashboard.deleteConfirmTitle || "Cancellare il ristorante?"}</h2>
          </div>
          <p className="text-sm text-zinc-300 mt-4">
            {(t.dashboard.deleteConfirmBody || "Questa operazione cancellerà definitivamente IL RISTORANTE, tutti i PIATTI, gli ORDINI e le TRANSAZIONI legate a esso. L'operazione è IRREVERSIBILE.")}
          </p>
          <div className="mt-4 text-xs text-zinc-400 font-mono">
            {(t.dashboard.deleteConfirmTypeHint || "Per confermare, digita")} <span className="text-red-300 font-bold">{rest.name}</span>:
          </div>
          <input
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="input-neon mt-2 border-red-500/40"
            placeholder={rest.name}
            data-testid="delete-confirm-input"
            autoFocus
          />
          <div className="mt-6 flex gap-2 flex-wrap">
            <button
              onClick={deleteRest}
              disabled={deleting || confirmText.trim() !== rest.name.trim()}
              className="btn-primary flex-1 justify-center !bg-red-500 hover:!bg-red-600"
              data-testid="delete-confirm-yes"
            >
              {deleting ? "…" : (t.dashboard.deleteYes || "Sì, cancella definitivamente")}
            </button>
            <button onClick={() => { setConfirmDel(false); setConfirmText(""); }} disabled={deleting} className="btn-outline" data-testid="delete-confirm-cancel">
              {t.dashboard.cancel || "Annulla"}
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-neon p-6 space-y-3">
        <h3 className="font-heading font-semibold text-lg">{t.dashboard.generalInfo}</h3>
        <Input label={t.dashboard.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="settings-name" />
        <Input label={t.dashboard.description} value={form.description} onChange={(v) => setForm({ ...form, description: v })} testId="settings-desc" />
        <Input label={t.dashboard.address} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testId="settings-address" />
        <Input label={t.dashboard.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="settings-phone" />
        <ImageUpload label={t.dashboard.logoUrl} value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} testId="settings-logo" aspect="square" />
        <ImageUpload label={t.dashboard.coverUrl} value={form.cover_url} onChange={(v) => setForm({ ...form, cover_url: v })} testId="settings-cover" aspect="wide" />
        <div>
          <label className="input-label">{t.dashboard.primaryColor}</label>
          <input type="color" value={form.primary_color || "#7C3AED"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-16 h-10 rounded-lg cursor-pointer bg-transparent" data-testid="settings-color" />
        </div>
      </div>
      <div className="card-neon p-6 space-y-3">
        <h3 className="font-heading font-semibold text-lg">{t.dashboard.orders} / {t.dashboard.menu}</h3>
        <OpeningHoursEditor
          value={form.opening_hours}
          onChange={(v) => setForm({ ...form, opening_hours: v })}
          t={t}
        />
        <Input label={t.dashboard.deliveryTime} type="number" value={form.delivery_time_min} onChange={(v) => setForm({ ...form, delivery_time_min: v })} testId="settings-delivery-time" />
        <Input label={t.dashboard.deliveryFee} type="number" step="0.01" value={form.delivery_fee} onChange={(v) => setForm({ ...form, delivery_fee: v })} testId="settings-delivery-fee" />
        <ToggleRow label={t.dashboard.tipToggle} value={form.tip_enabled} onChange={(v) => setForm({ ...form, tip_enabled: v })} testId="settings-tip-toggle" />
        <ToggleRow label={t.dashboard.urgencyToggle} value={form.urgency_enabled} onChange={(v) => setForm({ ...form, urgency_enabled: v })} testId="settings-urgency-toggle" />
        {form.urgency_enabled && (
          <>
            <Input label={t.dashboard.urgencyLabel} value={form.urgency_label} onChange={(v) => setForm({ ...form, urgency_label: v })} testId="settings-urgency-label" />
            <Input label={t.dashboard.urgencyFee} type="number" step="0.01" value={form.urgency_fee} onChange={(v) => setForm({ ...form, urgency_fee: v })} testId="settings-urgency-fee" />
          </>
        )}
        <Input label={t.dashboard.promoMessage} value={form.promo_message} onChange={(v) => setForm({ ...form, promo_message: v })} testId="settings-promo-msg" />
        <Input label={t.dashboard.promoDiscount} type="number" step="0.01" value={form.promo_discount_percent} onChange={(v) => setForm({ ...form, promo_discount_percent: v })} testId="settings-promo-discount" />

        <div className="border-t border-line pt-4 mt-2" data-testid="loyalty-section">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h4 className="font-heading font-semibold text-sm">{t.dashboard.loyalty?.title || "Punti fedeltà"}</h4>
            <label className="flex items-center gap-2 cursor-pointer text-xs font-mono uppercase tracking-widest">
              <input type="checkbox"
                checked={!!form.loyalty?.enabled}
                onChange={(e) => setForm({ ...form, loyalty: {
                  // Seed sensible defaults so the user can't enable loyalty without filling values
                  enabled: e.target.checked,
                  points_per_euro: form.loyalty?.points_per_euro ?? 1,
                  euro_per_point: form.loyalty?.euro_per_point ?? 0.05,
                  min_points_to_redeem: form.loyalty?.min_points_to_redeem ?? 20,
                } })}
                className="accent-[#00E5FF]"
                data-testid="loyalty-enabled"
              />
              <span className={(form.loyalty?.enabled) ? "text-neon-cyan" : "text-zinc-500"}>
                {(form.loyalty?.enabled) ? (t.dashboard.loyalty?.on || "Attivo") : (t.dashboard.loyalty?.off || "Spento")}
              </span>
            </label>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1 font-mono">{t.dashboard.loyalty?.hint || "Il cliente accumula punti su ogni ordine pagato e può scontarli al checkout."}</p>
          {form.loyalty?.enabled && (
            <div className="grid sm:grid-cols-3 gap-3 mt-3">
              <Input
                label={t.dashboard.loyalty?.pointsPerEuro || "Punti per ogni 1 €"}
                type="number" step="0.5"
                value={form.loyalty?.points_per_euro ?? 1}
                onChange={(v) => setForm({ ...form, loyalty: { ...(form.loyalty || {}), points_per_euro: parseFloat(v || 0) } })}
                testId="loyalty-points-per-euro"
              />
              <Input
                label={t.dashboard.loyalty?.euroPerPoint || "€ per ogni 1 punto"}
                type="number" step="0.01"
                value={form.loyalty?.euro_per_point ?? 0.05}
                onChange={(v) => setForm({ ...form, loyalty: { ...(form.loyalty || {}), euro_per_point: parseFloat(v || 0) } })}
                testId="loyalty-euro-per-point"
              />
              <Input
                label={t.dashboard.loyalty?.minRedeem || "Min. punti per riscatto"}
                type="number" step="1"
                value={form.loyalty?.min_points_to_redeem ?? 20}
                onChange={(v) => setForm({ ...form, loyalty: { ...(form.loyalty || {}), min_points_to_redeem: parseInt(v || 0) } })}
                testId="loyalty-min-redeem"
              />
            </div>
          )}
          {form.loyalty?.enabled && (
            <p className="text-[11px] text-neon-cyan mt-2 font-mono" data-testid="loyalty-preview">
              {(t.dashboard.loyalty?.preview || "Anteprima: {ppe} pt ogni 1 € speso · {ept} € di sconto per ogni punto")
                .replace("{ppe}", String(form.loyalty.points_per_euro ?? 1))
                .replace("{ept}", Number(form.loyalty.euro_per_point ?? 0.05).toFixed(2))}
            </p>
          )}
        </div>

        <button onClick={save} className="btn-primary mt-3 w-full justify-center" data-testid="settings-save-btn">
          {saved ? <><Check className="w-4 h-4" /> {t.dashboard.saved}</> : t.dashboard.save}
        </button>
      </div>
    </div>
    </>
  );
}

function PaymentsTab({ rest, t, onSaved }) {
  const [stripeId, setStripeId] = useState(rest.stripe_account_id || "");
  const [paypal, setPaypal] = useState(rest.paypal_email || "");
  const [paypalMe, setPaypalMe] = useState(rest.paypal_me || "");
  const [satispay, setSatispay] = useState(rest.satispay_handle || "");
  const [saved, setSaved] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connectErr, setConnectErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get("/payments/connect/status");
        setConnect(data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const save = async () => {
    await client.put(`/restaurants/${rest.id}`, { stripe_account_id: stripeId, paypal_email: paypal, paypal_me: paypalMe, satispay_handle: satispay });
    setSaved(true); setTimeout(() => setSaved(false), 1500);
    onSaved && onSaved();
  };

  const startConnect = async () => {
    setConnectErr("");
    try {
      const origin_url = window.location.origin;
      const { data } = await client.post("/payments/connect/onboard", { origin_url });
      window.location.href = data.onboarding_url;
    } catch (e) {
      setConnectErr(e?.response?.data?.detail || "Stripe Connect non disponibile");
    }
  };

  const isDemo = connect && connect.reason === "demo_key";

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="card-neon p-6" data-testid="connect-card">
        <h3 className="font-heading font-semibold text-lg">{t.dashboard.connectTitle}</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-4">{t.dashboard.connectSub}</p>
        {connect?.connected ? (
          <div className="space-y-2">
            <div className="chip chip-cyan inline-flex" data-testid="connect-status-connected">{t.dashboard.connectConnected}</div>
            <div className="text-xs font-mono text-zinc-400 break-all">{connect.account_id}</div>
            <div className="flex gap-3 text-xs mt-3">
              <span className={connect.charges_enabled ? "text-neon-cyan" : "text-zinc-500"}>{t.dashboard.connectCharges} {connect.charges_enabled ? "✓" : "✗"}</span>
              <span className={connect.payouts_enabled ? "text-neon-cyan" : "text-zinc-500"}>{t.dashboard.connectPayouts} {connect.payouts_enabled ? "✓" : "✗"}</span>
            </div>
          </div>
        ) : connect?.account_id ? (
          <div className="space-y-3">
            <div className="chip chip-yellow inline-flex" data-testid="connect-status-onboarding">{t.dashboard.connectContinue}</div>
            <button onClick={startConnect} className="btn-primary w-full justify-center" data-testid="connect-continue-btn">
              {t.dashboard.connectContinue}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <button onClick={startConnect} disabled={isDemo} className={`btn-primary w-full justify-center ${isDemo ? "opacity-40 cursor-not-allowed" : ""}`} data-testid="connect-start-btn">
              {t.dashboard.connectBtn}
            </button>
            {isDemo && <p className="text-xs text-amber-400" data-testid="connect-demo-notice">{t.dashboard.connectDisabledDemo}</p>}
            {connectErr && <p className="text-xs text-red-400" data-testid="connect-error">{connectErr}</p>}
          </div>
        )}
      </div>
      <div className="card-neon p-6">
        <h3 className="font-heading font-semibold text-lg">{t.dashboard.paymentSettings}</h3>
        <p className="text-sm text-zinc-400 mt-1 mb-4">{t.dashboard.paymentInfo}</p>
        <Input label={t.dashboard.stripeAccountId} value={stripeId} onChange={setStripeId} testId="payments-stripe-id" />
        <div className="h-3" />
        <Input label={t.dashboard.paypalEmail} value={paypal} onChange={setPaypal} testId="payments-paypal" />
        <div className="h-3" />
        <Input label={t.dashboard.paypalMe || "PayPal.Me (username, opzionale - genera QR)"} value={paypalMe} onChange={setPaypalMe} testId="payments-paypal-me" />
        <div className="h-3" />
        <Input label={t.dashboard.satispayHandle || "Satispay (telefono o handle)"} value={satispay} onChange={setSatispay} testId="payments-satispay" />
        <button onClick={save} className="btn-primary mt-5 w-full justify-center" data-testid="payments-save-btn">
          {saved ? <><Check className="w-4 h-4" /> {t.dashboard.saved}</> : t.dashboard.save}
        </button>
      </div>
    </div>
  );
}

function SubscriptionTab({ t, user }) {
  const [extras, setExtras] = useState(0);
  const [billing, setBilling] = useState({ company_name: "", vat_or_fiscal_code: "", address: "", email: "", pec_or_sdi: "", phone: "" });
  const [billingComplete, setBillingComplete] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);
  const [billingMsg, setBillingMsg] = useState(null);
  const [editingBilling, setEditingBilling] = useState(false);

  const loadBilling = async () => {
    try {
      const { data } = await client.get("/billing/me");
      const b = data.billing || {};
      setBilling({
        company_name: b.company_name || "",
        vat_or_fiscal_code: b.vat_or_fiscal_code || "",
        address: b.address || "",
        email: b.email || "",
        pec_or_sdi: b.pec_or_sdi || "",
        phone: b.phone || "",
      });
      setBillingComplete(!!data.complete);
      setEditingBilling(!data.complete);
    } catch { /* silent */ }
  };

  useEffect(() => { loadBilling(); }, []);

  const saveBilling = async (e) => {
    e?.preventDefault();
    setSavingBilling(true);
    setBillingMsg(null);
    try {
      const payload = { ...billing, pec_or_sdi: (billing.pec_or_sdi || "").trim() || "0000000" };
      await client.put("/billing/me", payload);
      setBilling(payload);
      setBillingComplete(true);
      setEditingBilling(false);
      setBillingMsg({ kind: "ok", msg: t.billing?.savedOk || "Dati di fatturazione salvati." });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && detail.message) || detail || e.message;
      setBillingMsg({ kind: "err", msg });
    } finally {
      setSavingBilling(false);
    }
  };

  const start = async (plan) => {
    if (!billingComplete) {
      setEditingBilling(true);
      setBillingMsg({ kind: "warn", msg: t.billing?.requiredBeforeCheckout || "Compila i dati di fatturazione prima di attivare l'abbonamento." });
      return;
    }
    const origin_url = window.location.origin;
    try {
      const { data } = await client.post("/payments/subscription", { plan, extra_restaurants: extras, origin_url });
      window.location.href = data.url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && detail.code === "billing_required") {
        setEditingBilling(true);
        setBillingMsg({ kind: "warn", msg: detail.message });
        return;
      }
      alert(detail?.message || detail || e.message);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="card-neon p-6 lg:col-span-3">
        <h3 className="font-heading font-semibold text-lg">{t.dashboard.subscriptionTitle}</h3>
        <div className="mt-2 text-sm text-zinc-400 flex items-center gap-2 flex-wrap">
          {user.subscription_status === "trial" && <span className="chip chip-yellow">{t.dashboard.trial}</span>}
          {user.subscription_status === "active" && <span className="chip chip-cyan">{t.dashboard.active}</span>}
          {user.subscription_status === "inactive" && <span className="chip">{t.dashboard.inactive}</span>}
          {user.subscription_until && <span className="font-mono">· {t.dashboard.until} {new Date(user.subscription_until).toLocaleDateString()}</span>}
        </div>
      </div>

      {/* BILLING DATA — mandatory before any paid plan */}
      <div className={`card-neon p-6 lg:col-span-3 ${!billingComplete ? "border-amber-400/60 shadow-[0_0_24px_-12px_#facc15]" : ""}`} data-testid="billing-card">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-mono">{t.billing?.kicker || "DATI DI FATTURAZIONE"}</div>
            <h4 className="font-heading font-semibold text-base mt-0.5">{t.billing?.title || "Dati per la fatturazione"}</h4>
            <p className="text-xs text-zinc-400 mt-1">{t.billing?.subtitle || "Obbligatori per attivare l'abbonamento. Servono a Renova per emettere le fatture mensili."}</p>
          </div>
          {billingComplete && !editingBilling && (
            <button onClick={() => setEditingBilling(true)} className="btn-outline !py-1.5 !px-3 text-xs" data-testid="billing-edit-btn">
              ✏️ {t.billing?.edit || "Modifica"}
            </button>
          )}
        </div>

        {billingMsg && (
          <div className={`text-xs rounded-lg p-3 mb-3 border ${billingMsg.kind === "ok" ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-200" : billingMsg.kind === "warn" ? "border-amber-500/40 bg-amber-500/10 text-amber-200" : "border-red-500/40 bg-red-500/10 text-red-200"}`} data-testid="billing-msg">
            {billingMsg.msg}
          </div>
        )}

        {editingBilling ? (
          <form onSubmit={saveBilling} className="grid sm:grid-cols-2 gap-3" data-testid="billing-form">
            <div className="sm:col-span-2">
              <label className="input-label">{t.billing?.companyLabel || "Ragione sociale / Nome e cognome *"}</label>
              <input className="input-neon" value={billing.company_name} onChange={(e) => setBilling({ ...billing, company_name: e.target.value })} placeholder="Es. Pizzeria da Mario SRL" data-testid="billing-company" required />
            </div>
            <div>
              <label className="input-label">{t.billing?.vatLabel || "P.IVA / Codice Fiscale *"}</label>
              <input className="input-neon" value={billing.vat_or_fiscal_code} onChange={(e) => setBilling({ ...billing, vat_or_fiscal_code: e.target.value })} placeholder="IT12345678901" data-testid="billing-vat" required />
            </div>
            <div>
              <label className="input-label">{t.billing?.phoneLabel || "Telefono *"}</label>
              <input className="input-neon" value={billing.phone} onChange={(e) => setBilling({ ...billing, phone: e.target.value })} placeholder="+39 123 456 7890" data-testid="billing-phone" required />
            </div>
            <div className="sm:col-span-2">
              <label className="input-label">{t.billing?.addressLabel || "Indirizzo *"}</label>
              <input className="input-neon" value={billing.address} onChange={(e) => setBilling({ ...billing, address: e.target.value })} placeholder="Via Roma 1, 00100 Roma" data-testid="billing-address" required />
            </div>
            <div>
              <label className="input-label">{t.billing?.emailLabel || "Email fatturazione *"}</label>
              <input type="email" className="input-neon" value={billing.email} onChange={(e) => setBilling({ ...billing, email: e.target.value })} placeholder="amministrazione@miosito.it" data-testid="billing-email" required />
            </div>
            <div>
              <label className="input-label">{t.billing?.pecLabel || "PEC o Codice SDI (0000000 se assenti) *"}</label>
              <input className="input-neon" value={billing.pec_or_sdi} onChange={(e) => setBilling({ ...billing, pec_or_sdi: e.target.value })} placeholder="0000000" data-testid="billing-pec-sdi" required />
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end mt-2">
              {billingComplete && (
                <button type="button" onClick={() => { setEditingBilling(false); setBillingMsg(null); loadBilling(); }} className="btn-outline !py-2 !px-3 text-xs" data-testid="billing-cancel-btn">
                  {t.billing?.cancel || "Annulla"}
                </button>
              )}
              <button type="submit" disabled={savingBilling} className="btn-primary !py-2 !px-4 text-xs" data-testid="billing-save-btn">
                {savingBilling ? "…" : (t.billing?.save || "Salva dati fatturazione")}
              </button>
            </div>
          </form>
        ) : (
          <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="billing-summary">
            <BillingRow label={t.billing?.companyLabel || "Ragione sociale"} value={billing.company_name} testid="billing-row-company" />
            <BillingRow label={t.billing?.vatLabel || "P.IVA / CF"} value={billing.vat_or_fiscal_code} testid="billing-row-vat" />
            <BillingRow label={t.billing?.phoneLabel || "Telefono"} value={billing.phone} testid="billing-row-phone" />
            <BillingRow label={t.billing?.addressLabel || "Indirizzo"} value={billing.address} testid="billing-row-address" />
            <BillingRow label={t.billing?.emailLabel || "Email fatturazione"} value={billing.email} testid="billing-row-email" />
            <BillingRow label={t.billing?.pecLabel || "PEC / SDI"} value={billing.pec_or_sdi} testid="billing-row-pec-sdi" />
          </dl>
        )}
      </div>

      <div className="card-neon p-6">
        <div className="chip chip-cyan inline-flex">{t.dashboard.planBasic.split(" ")[0]}</div>
        <div className="font-heading font-black text-4xl mt-3 text-neon-cyan text-glow-cyan">4,60€</div>
        <div className="text-sm text-zinc-500 font-mono">/mese</div>
        <button onClick={() => start("basic")} className="btn-outline w-full justify-center mt-5" data-testid="sub-basic-btn">{t.dashboard.activate}</button>
      </div>
      <div className="card-neon p-6 glow-violet">
        <div className="chip chip-violet inline-flex">{t.dashboard.planPro.split(" ")[0]}</div>
        <div className="font-heading font-black text-4xl mt-3 gradient-text-neon">9,90€</div>
        <div className="text-sm text-zinc-500 font-mono">/mese</div>
        <button onClick={() => start("pro")} className="btn-primary w-full justify-center mt-5" data-testid="sub-pro-btn">{t.dashboard.activate}</button>
      </div>
      <div className="card-neon p-6">
        <div className="chip chip-yellow inline-flex">{t.dashboard.extraRest}</div>
        <div className="font-heading font-black text-4xl mt-3 text-neon-yellow text-glow-yellow">+3,00€</div>
        <div className="text-sm text-zinc-500 font-mono">/mese cad.</div>
        <input type="number" min={0} value={extras} onChange={(e) => setExtras(Math.max(0, parseInt(e.target.value || 0)))} className="input-neon mt-4" data-testid="extras-input" />
      </div>
    </div>
  );
}

function BillingRow({ label, value, testid }) {
  return (
    <div className="py-1.5 border-b border-line/40 flex items-baseline gap-3" data-testid={testid}>
      <dt className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono w-32 flex-shrink-0">{label}</dt>
      <dd className="text-sm text-zinc-100 break-all">{value || <span className="text-zinc-600">—</span>}</dd>
    </div>
  );
}


function CommissionBanner({ t }) {
  const [summary, setSummary] = useState(null);
  const [paying, setPaying] = useState(false);

  const fetchSummary = async () => {
    try {
      const { data } = await client.get("/commissions/mine");
      setSummary(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchSummary();
    const id = setInterval(fetchSummary, 60000); // 1-min refresh
    return () => clearInterval(id);
  }, []);

  if (!summary) return null;
  const pending = Number(summary.pending_amount || 0);
  if (pending <= 0) return null;
  const threshold = Number(summary.threshold || 50);
  const pct = Math.min(100, Math.round((pending / threshold) * 100));
  const due = summary.due === true;

  const payNow = async () => {
    setPaying(true);
    try {
      const origin_url = window.location.origin;
      const { data } = await client.post("/payments/commission/checkout", { origin_url });
      window.location.href = data.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Errore avvio pagamento");
      setPaying(false);
    }
  };

  const labels = t.dashboard.commission || {};
  return (
    <div className={`card-neon p-4 mb-6 ${due ? "border-yellow-400/60" : "border-line"}`} data-testid="commission-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Wallet className={`w-4 h-4 ${due ? "text-yellow-300" : "text-neon-cyan"}`} />
            <span className={due ? "text-yellow-200" : "text-zinc-200"}>
              {labels.title || "Commissioni da saldare"}
            </span>
            <span className={`chip !py-0 !px-1.5 !text-[10px] ${due ? "chip-yellow" : "chip-cyan"}`} data-testid="commission-amount">
              € {pending.toFixed(2)} / € {threshold.toFixed(2)}
            </span>
          </div>
          <div className="mt-2 h-2 w-full rounded-full bg-deep-card overflow-hidden border border-line">
            <div
              className={`h-full transition-all ${due ? "bg-yellow-300 glow-yellow" : "bg-neon-cyan"}`}
              style={{ width: `${pct}%` }}
              data-testid="commission-bar"
            />
          </div>
          <p className="text-[11px] text-zinc-500 mt-1.5 font-mono">
            {due
              ? (labels.dueHint || "Hai superato la soglia di 50€: salda ora le commissioni dovute a Renova.")
              : (labels.hint || `Pagamento automatico al raggiungimento di € ${threshold.toFixed(0)}. Puoi anche pagare subito in qualsiasi momento.`)
            }
            {" "}({summary.orders_count} {labels.orders || "ordini in contanti"})
          </p>
        </div>
        <button
          onClick={payNow}
          disabled={paying}
          className={`${due ? "btn-yellow" : "btn-primary"} text-xs whitespace-nowrap`}
          data-testid="commission-pay-now"
        >
          {paying ? "..." : (labels.payNow || "Paga subito")}
        </button>
      </div>
    </div>
  );
}

const DAY_KEYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const DEFAULT_SLOT = { start: "12:00", end: "23:00" };

function normalizeSchedule(value) {
  // Accept legacy string (free text) → reset; accept partial arrays
  if (!Array.isArray(value)) value = [];
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = value[i] || {};
    const slots = Array.isArray(d.slots) ? d.slots : [];
    out.push({
      open: d.open === true && slots.length > 0,
      slots: slots.length > 0 ? slots : [{ ...DEFAULT_SLOT }],
    });
  }
  return out;
}

function OpeningHoursEditor({ value, onChange, t }) {
  const schedule = normalizeSchedule(value);
  const dayLabels = t.dashboard.dayNames || DAY_KEYS_IT;

  const update = (i, patch) => {
    const next = schedule.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };
  const setSlot = (i, idx, key, val) => {
    const next = schedule.slice();
    const slots = (next[i].slots || []).slice();
    slots[idx] = { ...slots[idx], [key]: val };
    next[i] = { ...next[i], slots };
    onChange(next);
  };
  const addSlot = (i) => {
    const next = schedule.slice();
    next[i] = { ...next[i], slots: [...(next[i].slots || []), { ...DEFAULT_SLOT }], open: true };
    onChange(next);
  };
  const removeSlot = (i, idx) => {
    const next = schedule.slice();
    const slots = (next[i].slots || []).slice();
    slots.splice(idx, 1);
    next[i] = { ...next[i], slots: slots.length ? slots : [{ ...DEFAULT_SLOT }], open: slots.length > 0 };
    onChange(next);
  };
  const copyMonToAll = () => {
    const tpl = schedule[0];
    const next = schedule.map(() => ({ open: tpl.open, slots: tpl.slots.map((s) => ({ ...s })) }));
    onChange(next);
  };
  const clearAll = () => {
    onChange(schedule.map(() => ({ open: false, slots: [{ ...DEFAULT_SLOT }] })));
  };

  return (
    <div data-testid="opening-hours-editor">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <label className="input-label">{t.dashboard.weeklyHours || "Orari di apertura"}</label>
        <div className="flex gap-2 text-[10px]">
          <button type="button" onClick={copyMonToAll} className="px-2 py-1 rounded border border-line text-zinc-400 hover:text-neon-cyan font-mono uppercase tracking-widest" data-testid="hours-copy-monday">
            {t.dashboard.copyMonToAll || "Copia Lun → tutti"}
          </button>
          <button type="button" onClick={clearAll} className="px-2 py-1 rounded border border-line text-zinc-400 hover:text-red-400 font-mono uppercase tracking-widest" data-testid="hours-clear-all">
            {t.dashboard.clearAll || "Svuota tutti"}
          </button>
        </div>
      </div>
      <p className="text-[10px] text-zinc-500 mt-1 font-mono">{t.dashboard.weeklyHoursHint || "Quando il ristorante è fuori orario, gli ordini vengono automaticamente rifiutati."}</p>
      <div className="mt-3 space-y-2">
        {schedule.map((day, i) => (
          <div key={i} className={`rounded-xl border p-3 ${day.open ? "border-neon-cyan/30 bg-neon-cyan/5" : "border-line bg-deep/40"}`} data-testid={`hours-day-${i}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 cursor-pointer min-w-[80px]">
                <input
                  type="checkbox"
                  checked={day.open}
                  onChange={(e) => update(i, { open: e.target.checked })}
                  className="accent-[#00E5FF]"
                  data-testid={`hours-day-${i}-toggle`}
                />
                <span className={`font-mono uppercase tracking-widest text-xs ${day.open ? "text-neon-cyan" : "text-zinc-500"}`}>{dayLabels[i]}</span>
              </label>
              {day.open ? (
                <div className="flex-1 space-y-1.5">
                  {(day.slots || []).map((sl, idx) => (
                    <div key={idx} className="flex items-center gap-2 flex-wrap">
                      <input
                        type="time"
                        value={sl.start || ""}
                        onChange={(e) => setSlot(i, idx, "start", e.target.value)}
                        className="input-neon !py-1 !text-xs w-24"
                        data-testid={`hours-day-${i}-slot-${idx}-start`}
                      />
                      <span className="text-zinc-500 text-xs">→</span>
                      <input
                        type="time"
                        value={sl.end || ""}
                        onChange={(e) => setSlot(i, idx, "end", e.target.value)}
                        className="input-neon !py-1 !text-xs w-24"
                        data-testid={`hours-day-${i}-slot-${idx}-end`}
                      />
                      {(day.slots || []).length > 1 && (
                        <button type="button" onClick={() => removeSlot(i, idx)} className="text-zinc-500 hover:text-red-400" data-testid={`hours-day-${i}-slot-${idx}-remove`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={() => addSlot(i)} className="text-[10px] text-neon-cyan hover:text-glow-cyan font-mono uppercase tracking-widest" data-testid={`hours-day-${i}-add`}>
                    + {t.dashboard.addSlot || "Aggiungi fascia"}
                  </button>
                </div>
              ) : (
                <span className="text-xs text-zinc-500 italic">{t.dashboard.closedDay || "Chiuso"}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
